/* =============================================================================
   campos.js — Selector y calendario con el estilo del sitio
   Hotel La Gruta · Arequipa

   El <select> y el <input type="date"> nativos no se pueden estilar: su
   lista desplegable y su calendario los dibuja el sistema operativo, con su
   fuente y su azul. Además, en una ventana angosta de escritorio el hint
   nativo "dd/mm/aaaa" se montaba sobre el overlay .avail-ph del móvil.

   Cómo está montado
   -----------------
   El control nativo NO se reemplaza: se queda en su sitio, invisible
   (opacity 0, sin eventos de puntero) y sigue siendo la fuente de verdad:
     - el valor que leen app.js y el formulario (yyyy-mm-dd en las fechas),
     - la validación `required` y su globo de aviso,
     - el foco con teclado y el nombre accesible (label[for]),
     - lo que usan los tests (selectOption / fill).
   Encima va un botón visual (.cf-btn, aria-hidden y fuera del orden de
   tabulación) que muestra el valor y abre el panel propio. Quien navega con
   teclado sigue sobre el control nativo: Enter, Espacio, Alt+↓ o F4 abren
   el mismo panel, y el foco entra en él.

   El panel vive en <body> (position: fixed) para que ningún overflow o
   transform de la página lo recorte, y se recoloca al hacer scroll.

   Llegada / salida: dentro del mismo contenedor (.avail o el formulario)
   la primera fecha es la llegada y la segunda la salida. En el calendario
   no se pueden elegir días pasados ni una salida anterior a la llegada.
   ============================================================================= */
(function () {
  'use strict';

  var OPEN = null;        // { panel, field, close } del panel abierto
  var uid = 0;

  function T(key, fallback) {
    var v = window.I18N && window.I18N.t(key);
    return v && v !== key ? v : fallback;
  }
  function lang() {
    return (window.I18N && window.I18N.getLang()) || document.documentElement.lang || 'es';
  }

  /* ── Fechas (siempre en hora local, sin pasar por UTC) ── */
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function toISO(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
  function fromISO(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
  }
  function today() { var d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  function addDays(d, n) { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }
  function sameDay(a, b) { return !!a && !!b && a.getTime() === b.getTime(); }
  /* Mismo orden que el hint avail.dateFormat (dd/mm/aaaa): lo que se ve al
     elegir coincide con lo que se veía vacío. */
  function display(d) {
    return pad(d.getDate()) + '/' + pad(d.getMonth() + 1) + '/' + d.getFullYear();
  }
  function cap(s) { return s.charAt(0).toUpperCase() + s.slice(1); }

  /* ── Infraestructura común de los paneles ── */
  function fire(el) {
    el.dispatchEvent(new Event('input', { bubbles: true }));
    el.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function place(panel, anchor) {
    var r = anchor.getBoundingClientRect();
    var vw = document.documentElement.clientWidth, vh = window.innerHeight;
    var w = panel.offsetWidth, h = panel.offsetHeight, gap = 8, edge = 16;
    var left = Math.min(Math.max(r.left, edge), vw - w - edge);
    var below = r.bottom + gap, above = r.top - gap - h;
    var top = (below + h > vh - edge && above > edge) ? above : below;
    panel.style.left = Math.max(edge, left) + 'px';
    panel.style.top = Math.max(edge, top) + 'px';
  }

  function openPanel(field, panel, focusFirst) {
    closePanel(false);
    document.body.appendChild(panel);
    field.wrap.classList.add('is-open');
    place(panel, field.wrap);
    requestAnimationFrame(function () { panel.classList.add('is-in'); });
    function reflow() { if (OPEN && OPEN.panel === panel) place(panel, field.wrap); }
    function outside(e) {
      if (!panel.contains(e.target) && !field.wrap.contains(e.target)) closePanel(false);
    }
    /* Tabular fuera del panel también lo cierra (sin devolver el foco). */
    function leave(e) {
      var to = e.relatedTarget;
      if (to && !panel.contains(to) && to !== field.native) closePanel(false);
    }
    window.addEventListener('scroll', reflow, true);
    window.addEventListener('resize', reflow);
    document.addEventListener('pointerdown', outside, true);
    panel.addEventListener('focusout', leave);
    OPEN = {
      panel: panel, field: field,
      cleanup: function () {
        window.removeEventListener('scroll', reflow, true);
        window.removeEventListener('resize', reflow);
        document.removeEventListener('pointerdown', outside, true);
      }
    };
    if (focusFirst) focusFirst();
  }

  function closePanel(returnFocus) {
    if (!OPEN) return;
    var o = OPEN; OPEN = null;
    o.cleanup();
    o.field.wrap.classList.remove('is-open');
    o.panel.remove();
    o.panel.classList.remove('is-in');
    if (returnFocus) o.field.native.focus({ preventScroll: true });
  }

  /* Botón visual sobre el control nativo. */
  function makeField(wrap, native, kind) {
    wrap.classList.add('cf', 'cf-' + kind);
    native.classList.add('cf-native');
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cf-btn';
    btn.tabIndex = -1;
    btn.setAttribute('aria-hidden', 'true');
    btn.innerHTML = '<span class="cf-val"></span>' + (kind === 'select'
      ? '<svg class="cf-ico" viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1.5 6 6.5 11 1.5"/></svg>'
      : '<svg class="cf-ico" viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="2.5" width="13" height="12" rx="1.5"/><path d="M1.5 6.5h13M5 1v3M11 1v3"/></svg>');
    wrap.appendChild(btn);
    var field = { wrap: wrap, native: native, btn: btn, val: btn.querySelector('.cf-val') };
    /* Solo foco de teclado: tras elegir con el mouse el foco vuelve al
       control nativo, y ahí no debe aparecer el anillo. */
    native.addEventListener('focus', function () {
      var kb = true;
      try { kb = native.matches(':focus-visible'); } catch (e) {}
      wrap.classList.toggle('is-focus', kb);
    });
    native.addEventListener('blur', function () { wrap.classList.remove('is-focus'); });
    return field;
  }

  /* ══ SELECT ══════════════════════════════════════════════════════════════ */
  function enhanceSelect(select) {
    var wrap = document.createElement('div');
    select.parentNode.insertBefore(wrap, select);
    wrap.appendChild(select);
    var f = makeField(wrap, select, 'select');

    function render() {
      var opt = select.options[select.selectedIndex];
      f.val.textContent = opt ? opt.textContent : '';
      wrap.classList.toggle('is-empty', !select.value && select.options[0] && select.options[0].value === '');
    }

    function open(fromKeyboard) {
      var id = 'cf-list-' + (++uid);
      var panel = document.createElement('div');
      panel.className = 'cf-panel cf-list';
      var ul = document.createElement('ul');
      ul.setAttribute('role', 'listbox');
      ul.id = id;
      var label = select.id && document.querySelector('label[for="' + select.id + '"]');
      if (label) ul.setAttribute('aria-label', label.textContent.replace('*', '').trim());
      ul.tabIndex = -1;
      var items = [];
      Array.prototype.forEach.call(select.options, function (o, i) {
        if (o.value === '' && i === 0 && o.textContent) return;   // "Seleccionar" no es una opción real
        var li = document.createElement('li');
        li.setAttribute('role', 'option');
        li.id = id + '-' + i;
        li.dataset.index = i;
        li.textContent = o.textContent;
        if (i === select.selectedIndex) { li.setAttribute('aria-selected', 'true'); li.classList.add('is-sel'); }
        li.addEventListener('click', function () { choose(i); });
        li.addEventListener('pointermove', function () { setActive(items.indexOf(li)); });
        items.push(li);
        ul.appendChild(li);
      });
      panel.appendChild(ul);
      panel.style.minWidth = Math.max(wrap.offsetWidth, 200) + 'px';
      var active = Math.max(0, items.findIndex(function (li) { return li.classList.contains('is-sel'); }));

      function setActive(n) {
        if (n < 0 || n >= items.length) return;
        if (items[active]) items[active].classList.remove('is-active');
        active = n;
        items[active].classList.add('is-active');
        ul.setAttribute('aria-activedescendant', items[active].id);
        items[active].scrollIntoView({ block: 'nearest' });
      }
      function choose(i) {
        if (select.selectedIndex !== i) { select.selectedIndex = i; fire(select); }
        render();
        closePanel(true);
      }
      ul.addEventListener('keydown', function (e) {
        var k = e.key;
        if (k === 'ArrowDown') setActive(active + 1);
        else if (k === 'ArrowUp') setActive(active - 1);
        else if (k === 'Home') setActive(0);
        else if (k === 'End') setActive(items.length - 1);
        else if (k === 'Enter' || k === ' ') choose(+items[active].dataset.index);
        else if (k === 'Escape') closePanel(true);
        else if (k === 'Tab') { closePanel(true); return; }
        else if (k.length === 1) {
          var n = items.findIndex(function (li, j) {
            return j > active && li.textContent.toLowerCase().indexOf(k.toLowerCase()) === 0;
          });
          if (n === -1) n = items.findIndex(function (li) { return li.textContent.toLowerCase().indexOf(k.toLowerCase()) === 0; });
          if (n !== -1) setActive(n);
          else return;
        } else return;
        e.preventDefault();
      });
      openPanel(f, panel, function () {
        setActive(active);
        ul.focus({ preventScroll: true });
      });
    }

    f.btn.addEventListener('click', function () {
      if (OPEN && OPEN.field === f) closePanel(false); else open(false);
    });
    select.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'F4' || (e.altKey && e.key === 'ArrowDown')) {
        e.preventDefault(); open(true);
      }
    });
    select.addEventListener('change', render);
    select.addEventListener('input', render);
    f.render = render;
    render();
    return f;
  }

  /* ══ CALENDARIO ══════════════════════════════════════════════════════════ */
  function enhanceDate(wrap, input) {
    var f = makeField(wrap, input, 'date');

    /* La pareja llegada/salida dentro del mismo contenedor. */
    function pair() {
      var box = input.closest('.avail, form') || document;
      var all = Array.prototype.slice.call(box.querySelectorAll('input.cf-native[type="date"]'));
      var i = all.indexOf(input);
      return { isOut: i === 1, other: all[i === 0 ? 1 : 0] || null };
    }

    function render() {
      var d = fromISO(input.value);
      f.val.textContent = d ? display(d) : T('avail.dateFormat', 'dd/mm/aaaa');
      wrap.classList.toggle('is-empty', !d);
    }

    function open() {
      var p = pair();
      var other = p.other && fromISO(p.other.value);
      var min = today();
      if (p.isOut && other) min = addDays(other, 1);
      var sel = fromISO(input.value);
      var start = p.isOut ? other : sel, end = p.isOut ? sel : other;   // llegada / salida
      if (!(start && end && end > start)) end = null;
      var focusDay = sel && sel >= min ? sel : min;
      var view = new Date(focusDay.getFullYear(), focusDay.getMonth(), 1);

      var panel = document.createElement('div');
      panel.className = 'cf-panel cf-cal';
      panel.setAttribute('role', 'dialog');
      var label = input.id && document.querySelector('label[for="' + input.id + '"]');
      if (label) panel.setAttribute('aria-label', label.textContent.replace('*', '').trim());

      var L = lang();
      var monthFmt = new Intl.DateTimeFormat(L, { month: 'long', year: 'numeric' });
      var dayFmt = new Intl.DateTimeFormat(L, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
      var wdFmt = new Intl.DateTimeFormat(L, { weekday: 'short' });

      panel.innerHTML =
        '<div class="cf-cal-head">' +
          '<button type="button" class="cf-nav" data-step="-1"><svg viewBox="0 0 8 12" aria-hidden="true"><path d="M6.5 1 1.5 6l5 5"/></svg></button>' +
          '<p class="cf-month" aria-live="polite"></p>' +
          '<button type="button" class="cf-nav" data-step="1"><svg viewBox="0 0 8 12" aria-hidden="true"><path d="M1.5 1l5 5-5 5"/></svg></button>' +
        '</div>' +
        '<div class="cf-week" aria-hidden="true"></div>' +
        '<div class="cf-grid" role="grid"></div>' +
        '<div class="cf-cal-foot"><button type="button" class="cf-clear"></button></div>';

      var navs = panel.querySelectorAll('.cf-nav');
      navs[0].setAttribute('aria-label', T('picker.prevMonth', 'Mes anterior'));
      navs[1].setAttribute('aria-label', T('picker.nextMonth', 'Mes siguiente'));
      var clearBtn = panel.querySelector('.cf-clear');
      clearBtn.textContent = T('picker.clear', 'Borrar');

      /* Semana de lunes a domingo (2024-01-01 fue lunes). */
      var week = panel.querySelector('.cf-week');
      for (var w = 0; w < 7; w++) {
        var s = document.createElement('span');
        s.textContent = wdFmt.format(new Date(2024, 0, 1 + w)).replace('.', '').slice(0, 2);
        week.appendChild(s);
      }

      var grid = panel.querySelector('.cf-grid');
      var monthEl = panel.querySelector('.cf-month');

      function draw(focusIt) {
        monthEl.textContent = cap(monthFmt.format(view));
        grid.innerHTML = '';
        var first = new Date(view.getFullYear(), view.getMonth(), 1);
        var lead = (first.getDay() + 6) % 7;
        var days = new Date(view.getFullYear(), view.getMonth() + 1, 0).getDate();
        var row;
        for (var c = 0; c < lead + days; c++) {
          if (c % 7 === 0) { row = document.createElement('div'); row.setAttribute('role', 'row'); row.className = 'cf-row'; grid.appendChild(row); }
          var cell = document.createElement('span');
          cell.setAttribute('role', 'gridcell');
          if (c >= lead) {
            var d = new Date(view.getFullYear(), view.getMonth(), c - lead + 1);
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'cf-day';
            b.textContent = d.getDate();
            b.dataset.iso = toISO(d);
            b.setAttribute('aria-label', dayFmt.format(d));
            b.tabIndex = sameDay(d, focusDay) ? 0 : -1;
            if (d < min) { b.disabled = true; b.classList.add('is-off'); }
            if (sameDay(d, today())) b.classList.add('is-today');
            if (sameDay(d, sel)) { b.classList.add('is-sel'); b.setAttribute('aria-pressed', 'true'); }
            if (sameDay(d, start) || sameDay(d, end)) b.classList.add('is-edge');
            if (start && end && d > start && d < end) b.classList.add('is-range');
            cell.appendChild(b);
          }
          row.appendChild(cell);
        }
        navs[0].disabled = view <= new Date(today().getFullYear(), today().getMonth(), 1);
        if (focusIt) {
          var t = grid.querySelector('[data-iso="' + toISO(focusDay) + '"]');
          if (t) t.focus({ preventScroll: true });
        }
        if (OPEN && OPEN.panel === panel) place(panel, f.wrap);
      }

      function move(n) {
        var d = addDays(focusDay, n);
        if (d < min) return;
        focusDay = d;
        if (d.getMonth() !== view.getMonth() || d.getFullYear() !== view.getFullYear()) view = new Date(d.getFullYear(), d.getMonth(), 1);
        draw(true);
      }

      function choose(d) {
        input.value = toISO(d);
        fire(input);
        render();
        /* Si la llegada nueva deja la salida antes o igual, la salida se
           vacía: mejor pedirla de nuevo que mandar un rango imposible. */
        if (!p.isOut && p.other) {
          var o = fromISO(p.other.value);
          if (o && o <= d) { p.other.value = ''; fire(p.other); }
        }
        closePanel(true);
      }

      navs.forEach(function (nb) {
        nb.addEventListener('click', function () {
          view = new Date(view.getFullYear(), view.getMonth() + (+nb.dataset.step), 1);
          var keep = new Date(view.getFullYear(), view.getMonth(), Math.min(focusDay.getDate(), 28));
          focusDay = keep < min ? min : keep;
          draw(false);
        });
      });
      grid.addEventListener('click', function (e) {
        var b = e.target.closest('.cf-day');
        if (b && !b.disabled) choose(fromISO(b.dataset.iso));
      });
      clearBtn.addEventListener('click', function () {
        if (input.value) { input.value = ''; fire(input); }
        render();
        closePanel(true);
      });
      panel.addEventListener('keydown', function (e) {
        var k = e.key;
        if (k === 'Escape') { closePanel(true); }
        else if (e.target.classList.contains('cf-day')) {
          if (k === 'ArrowLeft') move(-1);
          else if (k === 'ArrowRight') move(1);
          else if (k === 'ArrowUp') move(-7);
          else if (k === 'ArrowDown') move(7);
          else if (k === 'PageUp') move(-30);
          else if (k === 'PageDown') move(30);
          else return;
        } else return;
        e.preventDefault();
      });

      openPanel(f, panel, function () { draw(true); });
    }

    f.btn.addEventListener('click', function () {
      if (OPEN && OPEN.field === f) closePanel(false); else open();
    });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'F4' || (e.altKey && e.key === 'ArrowDown')) {
        e.preventDefault(); open();
      }
    });
    input.addEventListener('input', render);
    input.addEventListener('change', render);
    f.render = render;
    render();
    return f;
  }

  /* ── Arranque ── */
  var fields = [];
  document.querySelectorAll('.avail-date').forEach(function (wrap) {
    var input = wrap.querySelector('input[type="date"]');
    if (input) fields.push(enhanceDate(wrap, input));
  });
  document.querySelectorAll('select.avail-s, #reservaForm select').forEach(function (s) {
    fields.push(enhanceSelect(s));
  });

  /* Al cambiar de idioma: textos de las opciones y formato de fecha. */
  document.addEventListener('i18n:change', function () {
    closePanel(false);
    fields.forEach(function (f) { f.render(); });
  });
  /* Cambiar de página en la SPA deja el panel colgando de un campo oculto. */
  window.addEventListener('hashchange', function () { closePanel(false); });
})();
